import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Divider, Grid, IconButton, TextField, Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { GridActionsCellItem } from '@mui/x-data-grid';
import clsx from 'clsx';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React from 'react';

import { DataAPIContext } from '../../api';
import { DELETE } from '../../api/dataProvider';
import PageMeta from '../../components/PageMeta';
import RecordList from '../../components/RecordList';
import { ContextStore } from '../../store/contextStore';
import { useIsWidthUp } from '../../utils';
import DeleteButton from '../../components/DeleteButton';
import DeleteIconButton from '../../components/DeleteIconButton';
import BlacklistEditDialog from './BlacklistEditDialog';
import { actionLabel, deliveryTypeLabel, matchTypeLabel } from './constants';

const useStyles = makeStyles((theme) => ({
    rootMobile: {
        [theme.breakpoints.down('sm')]: {
            backgroundColor: '#FCFCFE',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            maxHeight: '100%',
            minHeight: 0,
            overflow: 'hidden',
            paddingBottom: `${theme.spacing(1)} !important`,
        },
    },
    mobilePageHeader: {
        [theme.breakpoints.down('sm')]: {
            flexShrink: 0,
            backgroundColor: '#FCFCFE',
            zIndex: 2,
        },
    },
    mobileListRegion: {
        [theme.breakpoints.down('sm')]: {
            flex: 1,
            minHeight: 0,
            maxHeight: 'calc(100dvh - 11rem)',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            paddingBottom: theme.spacing(1),
        },
    },
    recordListWrapper: {
        [theme.breakpoints.down('sm')]: {
            minHeight: 0,
        },
    },
    recordList: {
        border: 'none !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        height: `calc(${theme.cp.contentHeight} - 6rem) !important`,
    },
}));

const conditionLabel = (row) => {
    if (row.match_type === 'user') return row.user ? `user: ${row.user.email || row.user.id}` : 'user';
    if (row.match_type === 'ota') return row.ota ? `OTA: ${row.ota.name}` : 'OTA';
    if (row.match_type === 'reseller') return `reseller: ${row.pattern}`;
    if (row.match_type === 'sender') {
        if (row.sender_access_group) return `sender in: ${row.sender_access_group.name}`;
        return row.sender_user
            ? `sender: ${row.sender_user.email || row.sender_user.id}`
            : 'sender';
    }
    if (row.match_type === 'tag') {
        const n = (row.tags || []).length;
        return `tag: ${n} tag${n === 1 ? '' : 's'} ${(row.tag_match_mode || 'and').toUpperCase()}`;
    }
    return row.pattern;
};

// A multi-condition rule (CP-8268) cannot be summarised by its first condition
// alone -- naming only that would read as if the rule blocked more than it does.
const targetLabel = (row) => {
    const n = (row.conditions || []).length;
    if (n > 1) return `${n} conditions (${(row.condition_match_mode || 'all').toUpperCase()})`;
    return conditionLabel(row);
};

const eventsLabel = (row) => {
    const exc = (row.exception_event_codes || []).length + (row.exception_event_groups || []).length;
    const excSuffix = exc > 0 ? ` · ${exc} exempt` : '';
    if (row.block_all_events) return `All events${excSuffix}`;
    const n = (row.event_codes || []).length + (row.event_groups || []).length;
    return `${n} selected${excSuffix}`;
};

const BlacklistList = () => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm } = React.useContext(ContextStore);
    const { enqueueSnackbar } = useSnackbar();
    const isSmallScreen = !useIsWidthUp('sm');

    const [query, setQuery] = React.useState('');
    const [debounced, setDebounced] = React.useState('');
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [editOpen, setEditOpen] = React.useState(false);
    const [editId, setEditId] = React.useState(null);
    const [deleteRow, setDeleteRow] = React.useState(null);
    const [deleteReason, setDeleteReason] = React.useState('');
    const [deleting, setDeleting] = React.useState(false);

    const canWrite = hasPerm('blacklist_rule', 'write');
    const canDelete = hasPerm('blacklist_rule', 'delete');

    React.useEffect(() => {
        const t = setTimeout(() => setDebounced(query), 400);
        return () => clearTimeout(t);
    }, [query]);

    const openCreate = () => { setEditId(null); setEditOpen(true); };
    const openEdit = (id) => { setEditId(id); setEditOpen(true); };
    const refresh = () => setLastUpdate(new Date());

    const confirmDelete = () => {
        if (!deleteReason.trim()) {
            enqueueSnackbar('A reason is required to delete a rule', { variant: 'warning' });
            return;
        }
        setDeleting(true);
        dataAPI(DELETE, 'blacklist_rule', { id: deleteRow.id, data: { reason: deleteReason.trim() } })
            .then((res) => {
                if (res && res.statusCode && res.statusCode >= 400) {
                    enqueueSnackbar(res.body?.message || 'Delete failed', { variant: 'error' });
                    return;
                }
                enqueueSnackbar('Rule deleted', { variant: 'success' });
                setDeleteRow(null);
                setDeleteReason('');
                refresh();
            })
            .catch((e) => enqueueSnackbar(e?.body?.message || 'Delete failed', { variant: 'error' }))
            .finally(() => setDeleting(false));
    };

    const columns = React.useMemo(() => [
        { field: 'delivery_type', headerName: 'Delivery', flex: 0.7,
            valueGetter: (p) => deliveryTypeLabel(p.row.delivery_type) },
        { field: 'match_type', headerName: 'Match', flex: 0.8,
            valueGetter: (p) => matchTypeLabel(p.row.match_type) },
        { field: 'pattern', headerName: 'Target', flex: 1.4, sortable: false,
            renderCell: (p) => targetLabel(p.row) },
        { field: 'events', headerName: 'Events', flex: 1, sortable: false, filterable: false,
            renderCell: (p) => {
                const exc = (p.row.exception_event_codes || []).length + (p.row.exception_event_groups || []).length;
                const excChip = exc > 0
                    ? <Chip size="small" variant="outlined" color="success" label={`${exc} exempt`} sx={{ ml: 0.5 }} />
                    : null;
                if (p.row.block_all_events) return <>
                    <Chip size="small" label="All events" />{excChip}
                </>;
                const n = (p.row.event_codes || []).length + (p.row.event_groups || []).length;
                return <>
                    <Typography variant="body2" component="span">{n} selected</Typography>{excChip}
                </>;
            } },
        { field: 'is_active', headerName: 'Active', type: 'boolean', flex: 0.5 },
        { field: 'action', headerName: 'Action', flex: 0.8, sortable: false,
            renderCell: (p) => (p.row.action === 'cc'
                ? <Chip size="small" color="info" label="Deliver and Cc" />
                : <Typography variant="body2" component="span">Block</Typography>) },
        { field: 'cc_email', headerName: 'Cc', flex: 1, sortable: false },
        { field: 'redirect_to_email', headerName: 'Redirect to', flex: 1, sortable: false },
        { field: 'redirect_reply_to_email', headerName: 'Reply-To', flex: 1, sortable: false },
        { field: 'comment', headerName: 'Comment', flex: 1, sortable: false },
        { field: 'updated_ts', headerName: 'Updated', flex: 0.9,
            valueFormatter: (p) => (p.value ? moment(p.value).format('YYYY-MM-DD HH:mm') : '') },
        { field: 'actions', type: 'actions', headerName: '', width: 90, getActions: (params) => {
            const acts = [];
            if (canWrite) {
                acts.push(<GridActionsCellItem key="edit" icon={<EditIcon />} label="Edit"
                    onClick={() => openEdit(params.id)} />);
            }
            // CP-5545: disabled (grey), not dropped, without blacklist_rule.delete.
            acts.push(<DeleteIconButton key="del" allowed={canDelete}
                onClick={() => { setDeleteRow(params.row); setDeleteReason(''); }} />);
            return acts;
        } },
    ], [canWrite, canDelete]);

    const listItemRenderer = React.useCallback(
        (row, idx, rows) => {
            const updated = row.updated_ts ? moment(row.updated_ts).format('YYYY-MM-DD HH:mm') : '—';
            const openRow = () => openEdit(row.id);

            return (
                <React.Fragment key={row.id}>
                    <Grid container spacing={1} p={2} alignItems="center">
                        <Grid item xs={12}>
                            <Typography
                                style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}
                                fontWeight={600}
                                onClick={openRow}
                                sx={{ cursor: 'pointer' }}
                            >
                                {targetLabel(row)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sx={{ cursor: 'pointer' }} onClick={openRow}>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Delivery: {deliveryTypeLabel(row.delivery_type)} · {matchTypeLabel(row.match_type)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Events: {eventsLabel(row)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Active: {row.is_active ? 'Yes' : 'No'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Action: {actionLabel(row.action)}
                            </Typography>
                            {row.cc_email ? (
                                <Typography variant="body2" color="text.secondary" display="block">
                                    Cc: {row.cc_email}
                                </Typography>
                            ) : null}
                            {row.redirect_to_email ? (
                                <Typography variant="body2" color="text.secondary" display="block">
                                    Redirect to: {row.redirect_to_email}
                                </Typography>
                            ) : null}
                            {row.redirect_reply_to_email ? (
                                <Typography variant="body2" color="text.secondary" display="block">
                                    Reply-To: {row.redirect_reply_to_email}
                                </Typography>
                            ) : null}
                            {row.comment ? (
                                <Typography variant="body2" color="text.secondary" display="block"
                                    style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                    Comment: {row.comment}
                                </Typography>
                            ) : null}
                            <Typography variant="body2" color="text.secondary" display="block">
                                Updated: {updated}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} container justifyContent="flex-start" spacing={0.5} alignItems="center">
                            {canWrite && (
                                <Grid item>
                                    <IconButton onClick={(ev) => { ev.stopPropagation(); openRow(); }} aria-label="Edit rule" size="small">
                                        <EditIcon fontSize="small" color="primary" />
                                    </IconButton>
                                </Grid>
                            )}
                            <Grid item>
                                <DeleteIconButton
                                    title="Delete rule"
                                    allowed={canDelete}
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        setDeleteRow(row);
                                        setDeleteReason('');
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    {idx < rows.length - 1 ? <Divider /> : null}
                </React.Fragment>
            );
        },
        [canWrite, canDelete]
    );

    const blacklistRecordList = (
        <RecordList
            className={classes.recordList}
            resource="blacklist_rule"
            columns={columns}
            query={debounced}
            sortModel={[{ field: 'updated_ts', sort: 'desc' }]}
            deps={lastUpdate}
            checkboxSelection={false}
            onRowClick={(params) => openEdit(params.id)}
            listItemRenderer={isSmallScreen ? listItemRenderer : undefined}
            mobileNaturalHeight={isSmallScreen}
            disableListBorder={!isSmallScreen}
            noMatchesLabel="No blacklist rules"
            noItemsFoundMessage="No blacklist rules"
        />
    );

    const searchField = (
        <TextField size="small" placeholder="Search pattern / comment"
            value={query} onChange={(e) => setQuery(e.target.value)}
            fullWidth={isSmallScreen} />
    );

    return (
        <Box className={clsx(isSmallScreen && classes.rootMobile)} sx={isSmallScreen ? undefined : { p: 2 }}>
            <PageMeta title="Blacklists" />
            {isSmallScreen ? (
                <>
                    <Box className={classes.mobilePageHeader}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" px={1} pt={2}>
                            <Typography fontSize={20} fontWeight={500} lineHeight="24px">
                                Delivery blacklists
                            </Typography>
                        </Box>
                        {/* [+] at the end of the search row, as on Activities (CP-5563). */}
                        <Box px={1} pt={1.5} pb={1} display="flex" alignItems="center" gap={2}>
                            {searchField}
                            {canWrite && (
                                <Button variant="contained" color="primary" onClick={openCreate} aria-label="New rule">
                                    <AddIcon />
                                </Button>
                            )}
                        </Box>
                    </Box>
                    <Box className={classes.mobileListRegion}>
                        <Box className={classes.recordListWrapper}>{blacklistRecordList}</Box>
                    </Box>
                </>
            ) : (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
                        <Typography variant="h5">Delivery blacklists</Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {searchField}
                            {canWrite && (
                                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New rule</Button>
                            )}
                        </Box>
                    </Box>
                    {blacklistRecordList}
                </>
            )}

            <BlacklistEditDialog
                open={editOpen}
                ruleId={editId}
                onClose={() => setEditOpen(false)}
                onSaved={refresh}
            />

            <Dialog open={!!deleteRow} onClose={() => setDeleteRow(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete blacklist rule</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {deleteRow?.action === 'cc'
                            ? 'Matching emails will no longer be Cc\'d to this rule\'s addresses. Please state why.'
                            : 'This will stop blocking deliveries matched by this rule. Please state why.'}
                    </DialogContentText>
                    <TextField autoFocus fullWidth required label="Reason for deletion"
                        value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)}
                        error={!deleteReason.trim()} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteRow(null)}>Cancel</Button>
                    <DeleteButton disabled={deleting || !deleteReason.trim()}
                        onClick={confirmDelete}>Delete</DeleteButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BlacklistList;
